import LocaleService from 'infrastructure/i18n/LocaleService';
import GetDateFormatedService from 'modules/inbound/shared/domain/GetDateFormatedService';
import { IPagination } from 'modules/inbound/shared/domain/IPagination';
import {
  ITransferStockNextHoursDTO,
  ITransferStockNextHoursItemDTO
} from 'modules/inbound/transfer/infrastructure/dto/TransferStockNextHoursDTO';
import { CriticalStockType } from 'modules/shared/domain/enums';
import { TransferText } from '../infrastructure/i18n/TransferText';
import { TransferTranslations } from '../infrastructure/i18n/typing';

const transferTranslations: TransferTranslations = {
  ...TransferText[LocaleService.getLanguage()]
};

export interface ITransferStockNextHours {
  incomingMaterials: ITransferStockNextHoursItem[];
  columns: ColumnsInfo[];
}

export interface ITransferStockNextHoursItem {
  code: string;
  description: string;
  deliveryTime: string;
  warehouse: string;
  providerName: string | null;
  manufacturingSectionCode: string | null;
  lineGroupCode: string | null;
  providerDeliveryNote: string;
  plateNumber: string;
  numPackages: number;
  asnContainer: string;
  basicContainer: string;
  transportContainer: string;
  supplyContainer: string;
  supplyType: string;
  isPlanned: boolean;
  supplyCriticalType: CriticalStockType | null;
  supplyContainerCapacity?: number | null;
  supplyPoints?: string;
}

export interface ColumnsInfo {
  key: string;
  uiKey: string;
  value: string;
  sortable: boolean;
  isVisibleCheck: boolean;
}

export enum TransferStockNextHoursColumnKeys {
  code = 'code',
  description = 'description',
  deliveryTime = 'deliveryTime',
  warehouse = 'warehouse',
  providerDeliveryNote = 'providerDeliveryNote',
  plateNumber = 'plateNumber',
  numPackages = 'numPackages',
  isPlanned = 'isPlanned',
  supplyType = 'supplyType',
  asnContainer = 'asnContainer',
  basicContainer = 'basicContainer',
  transportContainer = 'transportContainer',
  supplyContainer = 'supplyContainer',
  providerName = 'providerName',
  supplyContainerCapacity = 'supplyContainerCapacity',
  manufacturingSectionCode = 'manufacturingSectionCode',
  lineGroupCode = 'lineGroupCode',
  supplyPoints = 'supplyPoints'
}

const SORTABLE_COLUMNS = [
  TransferStockNextHoursColumnKeys.code,
  TransferStockNextHoursColumnKeys.description,
  TransferStockNextHoursColumnKeys.deliveryTime,
  TransferStockNextHoursColumnKeys.warehouse,
  TransferStockNextHoursColumnKeys.providerDeliveryNote,
  TransferStockNextHoursColumnKeys.plateNumber,
  TransferStockNextHoursColumnKeys.numPackages,
  TransferStockNextHoursColumnKeys.supplyType,
  TransferStockNextHoursColumnKeys.asnContainer,
  TransferStockNextHoursColumnKeys.basicContainer,
  TransferStockNextHoursColumnKeys.transportContainer,
  TransferStockNextHoursColumnKeys.supplyContainer,
  TransferStockNextHoursColumnKeys.providerName,
  TransferStockNextHoursColumnKeys.isPlanned,
  TransferStockNextHoursColumnKeys.supplyContainerCapacity,
  TransferStockNextHoursColumnKeys.manufacturingSectionCode,
  TransferStockNextHoursColumnKeys.lineGroupCode,
  TransferStockNextHoursColumnKeys.supplyPoints
];

class TransferStockNextHours implements ITransferStockNextHours {
  incomingMaterials: ITransferStockNextHoursItem[];
  columns: ColumnsInfo[];
  pagination: IPagination;

  constructor({
    incomingMaterials,
    columns,
    pagination
  }: TransferStockNextHours) {
    this.incomingMaterials = incomingMaterials;
    this.columns = columns;
    this.pagination = pagination;
  }
  private static _camelToSnakeCase(key) {
    return key.replace(/([A-Z])/g, '_$1').toLowerCase();
  }

  private static _getColumnTranslation(key: string) {
    if (key === TransferStockNextHoursColumnKeys.supplyPoints) {
      return transferTranslations.nextHoursLocation;
    }

    return transferTranslations[key];
  }

  static generateFromDTO({
    incoming_materials,
    pagination
  }: ITransferStockNextHoursDTO): TransferStockNextHours {
    return new TransferStockNextHours({
      incomingMaterials: incoming_materials.map(
        ({
          code,
          description,
          delivery_time,
          provider_name,
          warehouse,
          manufacturing_section_code,
          line_group_code,
          provider_delivery_note,
          plate_number,
          num_packages,
          asn_container,
          basic_container,
          transport_container,
          supply_container,
          supply_type,
          is_planned,
          supply_critical_type,
          supply_container_capacity,
          supply_points
        }: ITransferStockNextHoursItemDTO): ITransferStockNextHoursItem => ({
          code,
          description: description ?? '-',
          deliveryTime: delivery_time
            ? GetDateFormatedService.getShortDateTime(delivery_time)
            : '-',
          providerName: provider_name ?? null,
          warehouse,
          manufacturingSectionCode: manufacturing_section_code ?? null,
          lineGroupCode: line_group_code ?? null,
          providerDeliveryNote: provider_delivery_note,
          plateNumber: plate_number,
          numPackages: num_packages,
          asnContainer: asn_container,
          basicContainer: basic_container,
          transportContainer: transport_container,
          supplyContainer: supply_container,
          supplyType: supply_type || '-',
          isPlanned: is_planned,
          supplyCriticalType: supply_critical_type ?? null,
          supplyContainerCapacity: supply_container_capacity ?? null,
          supplyPoints:
            Array.isArray(supply_points) && supply_points.length
              ? supply_points.filter(Boolean).join(' + ')
              : undefined
        })
      ),
      columns: Object.keys(TransferStockNextHoursColumnKeys).map((key) => ({
        key: this._camelToSnakeCase(key),
        uiKey: key,
        value: this._getColumnTranslation(key),
        sortable: SORTABLE_COLUMNS.includes(
          TransferStockNextHoursColumnKeys[key]
        ),
        isVisibleCheck: true,
        draggable: key !== TransferStockNextHoursColumnKeys.code
      })),
      pagination: {
        page: pagination.page,
        size: pagination.size,
        pageCount: pagination.page_count,
        totalCount: pagination.total_count
      }
    });
  }

  static generateEmpty() {
    return new TransferStockNextHours({
      incomingMaterials: [],
      columns: [],
      pagination: { page: 1, size: 10, pageCount: 0, totalCount: 0 }
    });
  }
}

export default TransferStockNextHours;
